import React from 'react';
import { useListForm } from '../../hooks/useListForm';
import { useSceneUpdate } from '../../hooks/useSceneUpdate';
import Conditionals from '../Conditionals';
import { getAllEndingsFromNovel } from '../../utils/endingsHelper';
import './InteractiveScenePrompts.css';

const INITIAL_DATA = { prompt_description: '', conditionals: [] };

const InteractiveScenePrompts = ({
  novelId, sceneId, scene, setScene, novel, loading, setLoading, error, setError, isProduction = false, sceneType = 'interactive'
}) => {
  const { showForm, editingIndex, formData, setFormData, openAdd, openEdit, close } = useListForm(INITIAL_DATA);
  const { saveScene } = useSceneUpdate(novelId, sceneId, isProduction, setScene, setLoading, sceneType);

  const handleEditPromptClick = (prompt, index) => {
    openEdit({ prompt_description: prompt.prompt_description, conditionals: prompt.conditionals || [] }, index);
  };

  const handleSavePrompt = async () => {
    if (!formData.prompt_description.trim()) {
      setError('Введите описание промпта');
      return;
    }
    setError(null);
    const updatedPrompts = [...(scene?.additional_prompt_descriptions || [])];
    const newPrompt = { prompt_description: formData.prompt_description.trim(), conditionals: formData.conditionals };
    if (editingIndex !== null) {
      updatedPrompts[editingIndex] = { ...updatedPrompts[editingIndex], ...newPrompt };
    } else {
      updatedPrompts.push(newPrompt);
    }
    const success = await saveScene({ ...scene, additional_prompt_descriptions: updatedPrompts }, 'Промпт успешно сохранен!');
    if (success) close();
  };

  const handleDeletePrompt = async (index) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот промпт?')) return;
    const updatedPrompts = [...(scene?.additional_prompt_descriptions || [])];
    updatedPrompts.splice(index, 1);
    await saveScene({ ...scene, additional_prompt_descriptions: updatedPrompts }, 'Промпт успешно удален!');
  };

  const totalChars = (scene?.additional_prompt_descriptions || [])
    .reduce((sum, p) => sum + (p.prompt_description || '').length, 0);
  const totalColor = totalChars > 10000 ? '#f87171' : totalChars > 8000 ? '#fbbf24' : 'rgba(255,255,255,0.55)';

  return (
    <div className="ispr-wrap">
      <div className="ispr-header">
        <h3 className="ispr-title">Промпты сцены</h3>
        <span style={{ fontSize: 13, fontFamily: 'Raleway, sans-serif', color: totalColor, fontWeight: 600 }}>
          {totalChars} / 10 000 симв.
        </span>
      </div>
      <p className="ispr-help">Добавьте контекстные промпты, которые предоставляют дополнительную информацию ИИ об этой сцене</p>

      <div className="ispr-add-wrap">
        <button className="ispr-add-btn" onClick={openAdd} disabled={loading}>
          <span className="button-icon">➕</span>
          Добавить промпт
        </button>
      </div>

      {error && <div className="ispr-error">{error}</div>}

      {showForm && (
        <div className="ispr-overlay">
          <div className="ispr-modal">
            <h3 className="ispr-modal-title">{editingIndex !== null ? 'Редактировать промпт' : 'Добавить промпт'}</h3>

            <div className="ispr-form-group">
              <label className="ispr-label">Описание промпта:</label>
              <textarea
                value={formData.prompt_description}
                onChange={(e) => setFormData({ ...formData, prompt_description: e.target.value })}
                className="ispr-textarea" disabled={loading} rows={4}
                placeholder="Введите дополнительный контекст или инструкции для этой сцены..."
                required
              />
              <div className="ispr-char-count">{formData.prompt_description.length} симв.</div>
            </div>

            <div className="ispr-form-group">
              <label className="ispr-label">Условия:</label>
              <Conditionals
                flags={novel?.states?.flags || []}
                scales={novel?.states?.scales || []}
                endings={getAllEndingsFromNovel(novel)}
                value={formData.conditionals}
                onChange={(conditionals) => setFormData({ ...formData, conditionals })}
                disabled={loading}
              />
            </div>

            <div className="ispr-form-actions">
              <button type="button" onClick={handleSavePrompt} disabled={loading} className="ispr-btn-primary">
                {loading ? 'Загрузка...' : 'Сохранить'}
              </button>
              <button type="button" onClick={() => { close(); setError(null); }} disabled={loading} className="ispr-btn-cancel">
                Отменить
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="ispr-list">
        {scene?.additional_prompt_descriptions && scene.additional_prompt_descriptions.length > 0 ? (
          scene.additional_prompt_descriptions.map((prompt, index) => (
            <div key={prompt.id || index} className="ispr-card">
              <div className="ispr-card-head">
                <h4 className="ispr-card-name">Промпт {index + 1} <span style={{ fontWeight: 400, fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>({(prompt.prompt_description || '').length} симв.)</span></h4>
                <div className="ispr-card-actions">
                  <button className="ispr-btn-edit" onClick={() => handleEditPromptClick(prompt, index)} disabled={loading}>
                    Редактировать промпт
                  </button>
                  <button className="ispr-btn-delete" onClick={() => handleDeletePrompt(index)} disabled={loading}>
                    Удалить
                  </button>
                </div>
              </div>
              <div className="ispr-card-body">
                <div className="prompt-details">
                  <p className="prompt-description">{prompt.prompt_description}</p>
                  {prompt.conditionals && prompt.conditionals.length > 0 && (
                    <p className="prompt-meta"><strong>Условия:</strong> {prompt.conditionals.length}</p>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="ispr-empty">
            <p>Для этой сцены пока нет дополнительных промптов.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InteractiveScenePrompts;
